/**
 * createSim — OWNER: core builder. Orchestrates the fixed tick (see ARCHITECTURE.md "Tick order").
 * Other builders should not need to edit this file; they implement their Subsystem.
 */
import type {
  Command,
  ContentRegistry,
  Ms,
  Rng,
  Sim,
  SimConfig,
  SimContext,
  SimEvent,
  SimEventInput,
  SimQueries,
  Subsystems,
  Unit,
  UnitId,
  Vec2,
  World,
  WorldOps,
} from '../contracts';
import { CHAT_MAX_LEN, JUMP_MS, TICK_MS } from '../contracts';
import { createAISystem } from '../ai/index';
import { createMechanicsSystem } from '../ai/mechanics/index'; // +encounters
import { createCombatSystem } from '../combat/index';
import { departUnit } from '../combat/deaths'; // +personas
import { startAutoAttack, stopAutoAttack } from '../combat/melee'; // +autoattack
import { maxHpMult } from '../combat/stats'; // +basekits
import { createCombatLog } from '../log/index';
import { createMPlusSystem } from '../mplus/index';
import { createSpatialSystem } from '../spatial/index';
import { createCastingSystem, refreshCooldowns } from './casting';
import { activeHighlights, resolveAbilityId } from './hotbar';
import { createRng } from './rng';
import { createEmptyWorld, createWorldOps, initialMPlusState, nextId } from './world';
import { defaultLooter, initWorldLoot, interactObject, tickWorldLoot } from '../worldloot/index'; // +world-resources
import { setGatherSkill, startGather } from '../field/gather'; // +prof
import { arrivalPoint, formationPoint, initField, releaseFieldHero, tickField, useFieldWaystone } from '../field/index'; // +field
import { applyHeroLevel, clampLevel } from '../field/heroes'; // +field
import { airspaceOf, hopFromPerch, hopToAir, updraftAt } from '../spatial/air'; // +glide

/** Default subsystem set (each implemented in its own folder). Overrides are used as given. */
export function createDefaultSubsystems(content: ContentRegistry, overrides: Partial<Subsystems> = {}): Subsystems {
  return {
    casting: overrides.casting ?? createCastingSystem(),
    combat: overrides.combat ?? createCombatSystem(),
    ai: overrides.ai ?? createAISystem(),
    spatial: overrides.spatial ?? createSpatialSystem(content),
    mplus: overrides.mplus ?? createMPlusSystem(),
    log: overrides.log ?? createCombatLog(),
    mechanics: overrides.mechanics ?? createMechanicsSystem(), // +encounters
  };
}

/** Party formation offsets around the entrance (tank front, healer/dps behind). */
const FORMATION: Vec2[] = [
  { x: 2, y: 0 },
  { x: -2, y: 0 },
  { x: 0, y: 2 },
  { x: 0, y: -2 },
];

export function createSim(config: SimConfig): Sim {
  const content = config.content;
  const dungeon = content.dungeon(config.dungeonId);
  const sys: Subsystems = createDefaultSubsystems(content, config.subsystems);

  let world: World = createEmptyWorld(config.seed, config.dungeonId, initialMPlusState(dungeon, config.keystone));
  const rng: Rng = createRng({
    get rngState() {
      return world.rngState;
    },
    set rngState(s) {
      world.rngState = s;
    },
  });

  // ctx is created once; `world`/`now` are live getters so restore() just swaps `world`.
  let ops: WorldOps;
  const ctx: SimContext = {
    get world() {
      return world;
    },
    rng,
    content,
    sys,
    get now(): Ms {
      return world.time;
    },
    emit(e: SimEventInput) {
      world.events.push({ ...e, timestamp: world.time } as SimEvent);
    },
    newId(prefix: string) {
      return nextId(world, prefix);
    },
    get ops() {
      return ops;
    },
  };
  ops = createWorldOps(() => world, content, (npcId) => sys.mplus.scaling(ctx, npcId), () => dungeon?.mobLevel); // +field: mob level

  // ---- setup ---------------------------------------------------------------
  sys.mplus.init(ctx, config.keystone);
  sys.spatial.init(ctx);
  // +field: field maps put the party at the arrival point (SimConfig.arrive, else the entrance).
  const arrival = dungeon?.field ? arrivalPoint(dungeon, config.arrive) : null;
  const entrance = arrival?.pos ?? dungeon?.entrance ?? { x: 0, y: 0 };
  const facing = arrival?.facing ?? dungeon?.entranceFacing ?? 0;
  config.party.forEach((m, i) => {
    const off = FORMATION[i % FORMATION.length] ?? { x: 0, y: 0 };
    const u = ops.spawnPlayer({
      classId: m.classId,
      name: m.name,
      controller: m.controller,
      pos: arrival ? formationPoint(ctx, entrance, i) : { x: entrance.x + off.x, y: entrance.y + off.y },
      facing,
      id: m.id ?? `p${i + 1}`,
    });
    // +field: hero level scales max HP / power and gates abilities.
    const cls = content.class(m.classId);
    if (m.level !== undefined && Number.isFinite(m.level) && cls) applyHeroLevel(u, cls, m.level, true);
    world.partyIds.push(u.id);
  });
  // Class passives (tank threat aura, mastery...) are permanent auras applied at spawn.
  for (const id of world.partyIds) {
    const classId = world.units[id]?.classId;
    const def = classId ? content.class(classId) : undefined;
    for (const aura of def?.passives ?? []) sys.combat.applyAura(ctx, id, aura.id, id, aura.id);
  }
  if (dungeon && config.spawnPacks !== false) {
    for (const p of dungeon.packs) {
      world.packs[p.id] = { id: p.id, memberIds: [], state: 'idle', engagedAt: null };
      for (const s of p.spawns) ops.spawnNpc({ npcId: s.npcId, pos: s.pos, facing: s.facing, packId: p.id });
    }
  }
  // +field: open fields fill their spawn points and apply the role auras.
  if (dungeon?.field) initField(ctx, dungeon, arrival ?? undefined);
  // +world-resources: breakables / caches / nodes; only the looter breaks and collects.
  if (config.worldObjects !== false) initWorldLoot(world, dungeon, config.looterId === undefined ? defaultLooter(world) : config.looterId);
  if (config.autoStart) sys.mplus.startRun(ctx);
  // Setup events (spawns, run_start) are flushed on the first step.

  // ---- commands --------------------------------------------------------------
  /** Validate and apply one Command. Commands for AI-controlled units are ignored. */
  function applyCommand(cmd: Command): void {
    const u = world.units[cmd.actorId];
    if (!u || u.controller === 'ai' || u.departed) return;
    if (cmd.type === 'setLevel') return applySetLevel(u, cmd.level); // +field
    if (cmd.type === 'setGatherSkill') return setGatherSkill(ctx, cmd); // +prof
    // +personas: chat, presence and leaving have no gameplay effect beyond their own state.
    if (cmd.type === 'say' || cmd.type === 'presence' || cmd.type === 'leaveParty') return applySocial(cmd, u);
    if (cmd.type === 'interact') {
      if (cmd.what === 'break' && cmd.targetId) return void interactObject(ctx, u.id, cmd.targetId); // +world-resources
      if (cmd.what?.startsWith('waystone:')) return void useFieldWaystone(ctx, u.id, cmd.what.slice(9)); // +zone
      if (cmd.what?.startsWith('gather:')) return void startGather(ctx, u.id, cmd.what.slice(7)); // +prof
      if (cmd.what === 'startRun' || cmd.what === undefined) sys.mplus.startRun(ctx);
      else if (cmd.what === 'releaseSpirit' && !u.alive) {
        if (world.field) releaseFieldHero(ctx, u.id); // +field
        else sys.mplus.releaseSpirit?.(ctx, u.id);
      }
      return;
    }
    if (!u.alive) return;
    switch (cmd.type) {
      case 'jump': {
        // +jump: hop (momentum kept); refused mid-air, while rooted / stunned or being moved.
        if (u.jump || u.move?.mode === 'forced' || !sys.combat.controlState(ctx, u.id).canMove) return;
        if (u.cast) {
          const ab = content.ability(u.cast.abilityId);
          if (!ab?.canCastWhileMoving) sys.casting.cancelCast(ctx, u.id, 'moved');
        }
        if (u.perch) hopFromPerch(ctx, u, world.time + JUMP_MS); // +glide: a hop from a platform is free flight
        else u.jump = { at: world.time, until: world.time + JUMP_MS, pending: null };
        ctx.emit({ type: 'jump', sourceID: u.id });
        return;
      }
      case 'glide':
        return applyGlide(u, cmd.on === true); // +glide
      case 'move': {
        // Only a human's input starts the timer; bots (formation moves) start it via the gate check.
        if (world.mplus.phase === 'pre' && u.controller === 'human') sys.mplus.startRun(ctx);
        if (u.move?.mode === 'forced' || !sys.combat.controlState(ctx, u.id).canMove) return;
        // +jump: no air control: the latest move waits for the landing.
        if (u.jump) {
          u.jump.pending = cmd.dest && isFiniteVec(cmd.dest) ? { dest: { ...cmd.dest } } : cmd.dir && isFiniteVec(cmd.dir) && (cmd.dir.x !== 0 || cmd.dir.y !== 0) ? { dir: { ...cmd.dir } } : { stop: true };
          return;
        }
        if (cmd.dest && isFiniteVec(cmd.dest)) sys.spatial.setDestination(ctx, u.id, cmd.dest);
        else if (cmd.dir && isFiniteVec(cmd.dir) && (cmd.dir.x !== 0 || cmd.dir.y !== 0)) {
          const len = Math.hypot(cmd.dir.x, cmd.dir.y);
          u.move = { mode: 'dir', dir: { x: cmd.dir.x / len, y: cmd.dir.y / len } };
        } else u.move = null;
        return;
      }
      case 'stop':
        if (u.jump) u.jump.pending = { stop: true }; // +jump: lands, then stops
        else if (u.move?.mode !== 'forced') u.move = null;
        return;
      case 'face': {
        if (!sys.combat.controlState(ctx, u.id).canAct) return;
        if (cmd.targetId) {
          const t = world.units[cmd.targetId];
          if (t && t.id !== u.id) u.facing = Math.atan2(t.pos.y - u.pos.y, t.pos.x - u.pos.x);
        } else if (cmd.facing !== undefined && Number.isFinite(cmd.facing)) u.facing = normalizeAngle(cmd.facing);
        return;
      }
      case 'target':
        if (cmd.targetId === null || world.units[cmd.targetId]) u.targetId = cmd.targetId;
        return;
      case 'cast':
        sys.casting.tryCast(ctx, u.id, cmd.abilityId, cmd.targetId ?? u.targetId ?? null, cmd.point ?? null);
        return;
      case 'cancelCast':
        if (u.cast) sys.casting.cancelCast(ctx, u.id, 'player');
        u.queued = null;
        return;
      case 'autoAttack': // +autoattack
        return applyAutoAttack(u, cmd.on, cmd.targetId);
    }
  }

  /**
   * +autoattack: Command 'autoAttack' (Attack key / right-click on a hostile). `targetId` selects
   * first. A caster's staff starts only in melee range (otherwise the click just targets).
   */
  function applyAutoAttack(u: Unit, on: boolean, targetId: UnitId | undefined): void {
    if (typeof targetId === 'string' && world.units[targetId]) u.targetId = targetId;
    if (!on) return stopAutoAttack(u);
    if (u.swing?.kind === 'staff' && !(u.targetId && sys.spatial.inMeleeRange(world, u.id, u.targetId))) return;
    startAutoAttack(ctx, u);
  }

  /** +field: Command 'setLevel' — player units only, higher levels only. */
  function applySetLevel(u: Unit, level: number): void {
    const cls = u.classId ? content.class(u.classId) : undefined;
    if (u.kind !== 'player' || !cls || !Number.isFinite(level)) return;
    const next = clampLevel(level);
    const prev = u.level;
    if (prev !== undefined && next <= prev) return;
    applyHeroLevel(u, cls, next, true, maxHpMult(ctx, u)); // +basekits: keep Hearthward's +max HP
    ctx.emit({ type: 'level_up', sourceID: u.id, level: next, gained: prev !== undefined ? next - prev : 1 });
  }

  /**
   * +glide: Command 'glide'. Opens the glider of an airborne hero (a hop turns into free flight at
   * its current height and rate); folds it (refused inside an updraft). Players only.
   */
  function applyGlide(u: Unit, on: boolean): void {
    const j = u.jump;
    if (!j || u.kind !== 'player' || u.controller !== 'human') return;
    if (on) {
      if (j.air?.glide || j.air?.landed) return;
      if (u.move?.mode === 'forced' || !sys.combat.controlState(ctx, u.id).canMove) return;
      const a = hopToAir(ctx, u, true);
      if (!a) return;
      a.glide = true;
      delete a.folded;
      ctx.emit({ type: 'glide', sourceID: u.id, on: true });
      return;
    }
    const a = j.air;
    if (!a?.glide || a.landed) return;
    if (updraftAt(airspaceOf(content, world.dungeonId), u.pos, a.z)) return; // the wind holds it open
    a.glide = false;
    a.folded = true;
    ctx.emit({ type: 'glide', sourceID: u.id, on: false });
  }

  /** +jump: units whose jump ends this tick land (and take the move sent mid-air). */
  function landJumps(): void {
    for (const id of world.partyIds) {
      const u = world.units[id];
      if (!u?.jump) continue;
      // +glide: free flight lands when the spatial step touched down, not on a clock.
      const air = u.jump.air;
      if (air ? !air.landed : world.time < u.jump.until) continue;
      const pending = u.jump.pending;
      delete u.jump;
      if (air) {
        if (air.glide) ctx.emit({ type: 'glide', sourceID: u.id, on: false });
        ctx.emit({ type: 'land', sourceID: u.id, drop: Math.round((air.peak - air.z) * 100) / 100, ...(u.perch ? { platformId: u.perch.platformId } : {}) });
        // A click-to-move path from before the flight is re-planned from where the flyer came down.
        if (!pending && u.move?.mode === 'dest' && u.alive) sys.spatial.setDestination(ctx, u.id, u.move.dest);
      } else ctx.emit({ type: 'land', sourceID: u.id, ...(u.perch ? { platformId: u.perch.platformId } : {}) });
      if (!pending || !u.alive || u.move?.mode === 'forced') continue;
      if (pending.stop) u.move = null;
      else if (pending.dest) sys.spatial.setDestination(ctx, u.id, pending.dest);
      else if (pending.dir) {
        const len = Math.hypot(pending.dir.x, pending.dir.y);
        u.move = { mode: 'dir', dir: { x: pending.dir.x / len, y: pending.dir.y / len } };
      }
    }
  }

  /** +personas: 'say' → chat event; 'presence' → Unit.presence; 'leaveParty' → departUnit. */
  function applySocial(cmd: Extract<Command, { type: 'say' | 'presence' | 'leaveParty' }>, u: Unit): void {
    if (u.kind !== 'player') return;
    if (cmd.type === 'say') {
      const text = typeof cmd.text === 'string' ? cmd.text.trim().slice(0, CHAT_MAX_LEN) : '';
      if (!text) return;
      ctx.emit({ type: 'chat', sourceID: u.id, text, ...(cmd.lineId ? { lineId: String(cmd.lineId).slice(0, 64) } : {}), ...(cmd.emote ? { emote: true } : {}) });
      return;
    }
    if (cmd.type === 'presence') {
      const status = cmd.status === 'afk' || cmd.status === 'wandering' ? cmd.status : null;
      if ((u.presence ?? null) === status) return;
      if (status) u.presence = status;
      else delete u.presence;
      ctx.emit({ type: 'presence', sourceID: u.id, status });
      return;
    }
    if (!departUnit(ctx, u.id)) return;
    ctx.emit({
      type: 'party_leave',
      sourceID: u.id,
      reason: cmd.reason ?? 'abandon',
      ...(cmd.blameId ? { blameId: cmd.blameId } : {}),
      ...(cmd.cause ? { cause: String(cmd.cause).slice(0, CHAT_MAX_LEN) } : {}),
    });
  }

  // ---- tick ------------------------------------------------------------------
  function step(commands: readonly Command[] = []): SimEvent[] {
    world.tick += 1;
    world.time = world.tick * TICK_MS;
    // 1. commands (deterministic order: as given)
    for (const c of commands) applyCommand(c);
    // 2. cooldown charges
    refreshCooldowns(ctx);
    // 3. NPC brains + encounter state
    sys.ai.update(ctx);
    sys.ai.updateEncounter(ctx);
    // 4. casts / channels / spell queue
    sys.casting.update(ctx);
    // 5. movement
    sys.spatial.moveUnits(ctx, TICK_MS);
    landJumps(); // +jump
    // 6. auras, zones, resources
    sys.combat.tickAuras(ctx);
    sys.combat.tickZones(ctx);
    sys.combat.tickResources(ctx);
    // 7. melee swings
    sys.combat.tickMelee(ctx);
    // 8. telegraph resolution
    sys.combat.tickTelegraphs(ctx);
    // 9. deaths
    sys.combat.resolveDeaths(ctx);
    // 9b. +world-resources: looter hits break clutter, caches open, pickups magnet/collect
    tickWorldLoot(ctx);
    // 9c. +field: kills, respawns, corpses, hero release / regen / portals (no-op off field maps)
    if (world.field) tickField(ctx);
    // 10. M+ bookkeeping (timer, combat segments, wipes, completion)
    sys.mplus.update(ctx);
    // 11. flush
    const events = world.events;
    world.events = [];
    sys.log.ingest(events, world);
    return events;
  }

  const queries: SimQueries = {
    canCast: (casterId, abilityId, targetId, point) => sys.casting.canCast(ctx, casterId, abilityId, targetId, point),
    cooldownRemaining: (unitId, abilityId) => sys.casting.cooldownRemaining(world, unitId, abilityId),
    gcdRemaining: (unitId) => Math.max(0, (world.units[unitId]?.gcdUntil ?? 0) - world.time),
    effectiveStats: (unitId) => sys.combat.effectiveStats(ctx, unitId),
    controlState: (unitId) => sys.combat.controlState(ctx, unitId),
    resolveAbility: (unitId, abilityId) => resolveAbilityId(content, world, unitId, abilityId), // +lead (F2)
    activeHighlights: (unitId) => activeHighlights(content, world, unitId), // +lead (F11)
    spatial: sys.spatial,
  };

  return {
    content,
    log: sys.log,
    queries,
    step,
    getWorld: () => world,
    snapshot: () => structuredClone(world),
    restore(snap: World) {
      world = structuredClone(snap);
      sys.spatial.init(ctx);
    },
    result: () => sys.mplus.result(world),
  };
}

const isFiniteVec = (v: Vec2): boolean => Number.isFinite(v.x) && Number.isFinite(v.y);

/** Wrap to (-PI, PI]. */
export function normalizeAngle(a: number): number {
  const t = 2 * Math.PI;
  let r = a % t;
  if (r <= -Math.PI) r += t;
  else if (r > Math.PI) r -= t;
  return r;
}
